import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const ContactForm = () => {
  const { t } = useTranslation();

  const contactSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.nameRequired") })
          .max(100, { message: t("contactForm.errors.nameMax") }),
        email: z
          .string()
          .trim()
          .email({ message: t("contactForm.errors.emailInvalid") })
          .max(255, { message: t("contactForm.errors.emailMax") }),
        company: z
          .string()
          .trim()
          .max(100, { message: t("contactForm.errors.companyMax") })
          .optional(),
        phone: z
          .string()
          .trim()
          .max(20, { message: t("contactForm.errors.phoneMax") })
          .optional(),
        message: z
          .string()
          .trim()
          .min(1, { message: t("contactForm.errors.messageRequired") })
          .max(1000, { message: t("contactForm.errors.messageMax") }),
      }),
    [t]
  );

  type ContactFormValues = z.infer<typeof contactSchema>;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = (data: ContactFormValues) => {
    const notProvided = t("contactForm.mailto.notProvided");
    const subject = encodeURIComponent(t("contactForm.mailto.subject"));
    const body = encodeURIComponent(
      `${t("contactForm.mailto.name")}: ${data.name}\n` +
      `${t("contactForm.mailto.email")}: ${data.email}\n` +
      `${t("contactForm.mailto.company")}: ${data.company || notProvided}\n` +
      `${t("contactForm.mailto.phone")}: ${data.phone || notProvided}\n\n` +
      `${t("contactForm.mailto.message")}:\n${data.message}`
    );

    trackEvent("generate_lead", {
      form_location: "contact_page",
      has_company: Boolean(data.company),
      has_phone: Boolean(data.phone),
    });

    window.location.href = `mailto:info@ailabbet.com?subject=${subject}&body=${body}`;

    toast.success(t("contactForm.toastOpening"));
    form.reset();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-8">
      <h2 className="text-2xl md:text-3xl font-bold mb-6">{t("contactForm.heading")}</h2>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.name.label")} *</FormLabel>
                <FormControl>
                  <Input placeholder={t("contactForm.fields.name.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.email.label")} *</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={t("contactForm.fields.email.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.company.label")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("contactForm.fields.company.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.phone.label")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("contactForm.fields.phone.placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("contactForm.fields.message.label")} *</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t("contactForm.fields.message.placeholder")}
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" variant="primary" size="lg" className="w-full">
            {t("contactForm.submit")}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
